import { createContext, ReactNode, useCallback, useContext, useReducer } from "react";
import { ViewOptionStateEnum } from "../../components/contextual-app-bar/ViewOptionStateEnum";

export enum UICustomizationPresetAdminViewTabEnum {
    None = -1,
    Presets = 0
}

export type UICustomizationPresetAdminStateType = {
    activeTab: UICustomizationPresetAdminViewTabEnum;
    organizationID: number | undefined;
    presetCount: number;
    viewOptionState: ViewOptionStateEnum;
};

enum UICustomizationPresetAdminActionType {
    ActiveTabChanged = "ACTIVE_TAB_CHANGED",
    OrganizationChanged = "ORGANIZATION_CHANGED",
    PresetCountChanged = "PRESET_COUNT_CHANGED",
    ViewOptionStateChanged = "VIEW_OPTION_STATE_CHANGED",
    ClearCountsChanged = "CLEAR_COUNTS_CHANGED",
}

type UICustomizationPresetAdminAction = {
    type: UICustomizationPresetAdminActionType.ActiveTabChanged;
    activeTab: UICustomizationPresetAdminViewTabEnum;
} | {
    type: UICustomizationPresetAdminActionType.OrganizationChanged;
    organizationID: number | undefined;
} | {
    type: UICustomizationPresetAdminActionType.PresetCountChanged,
    count: number;
} | {
    type: UICustomizationPresetAdminActionType.ClearCountsChanged,
    payload: void;
} | {
    type: UICustomizationPresetAdminActionType.ViewOptionStateChanged,
    viewOptionState: ViewOptionStateEnum;
};

const initialState: UICustomizationPresetAdminStateType = {
    activeTab: UICustomizationPresetAdminViewTabEnum.Presets,
    organizationID: undefined,
    presetCount: 0,
    viewOptionState: ViewOptionStateEnum.Default,
};

function reducer(state: UICustomizationPresetAdminStateType, action: UICustomizationPresetAdminAction): UICustomizationPresetAdminStateType {
    switch (action.type) {

        case UICustomizationPresetAdminActionType.ActiveTabChanged:
            return {
                ...state,
                activeTab: action.activeTab
            };

        case UICustomizationPresetAdminActionType.OrganizationChanged:
            return {
                ...state,
                organizationID: action.organizationID
            };

        case UICustomizationPresetAdminActionType.PresetCountChanged:
            return {
                ...state,
                presetCount: action.count
            };

        case UICustomizationPresetAdminActionType.ViewOptionStateChanged:
            return {
                ...state,
                viewOptionState: action.viewOptionState
            };

        case UICustomizationPresetAdminActionType.ClearCountsChanged:
            return {
                ...state,
                presetCount: 0
            };

        default:

            return state;
    }
}

export type UseUICustomizationPresetAdminStateType = {
    state: UICustomizationPresetAdminStateType;
    setActiveTab: (activeTab: UICustomizationPresetAdminViewTabEnum) => void;
    setOrganizationID: (organizationID: number | undefined) => void;
    setPresetCount: (count: number) => void;
    setViewOptionState: (state: ViewOptionStateEnum) => void;
    clearCounts: () => void;
};

export function useUICustomizationPresetAdminState(): UseUICustomizationPresetAdminStateType {

    const [state, dispatch] = useReducer(reducer, initialState);

    const setActiveTab = useCallback((activeTab: UICustomizationPresetAdminViewTabEnum) => dispatch({
        type: UICustomizationPresetAdminActionType.ActiveTabChanged,
        activeTab: activeTab
    }), []);

    const setOrganizationID = useCallback((organizationID: number | undefined) => dispatch({
        type: UICustomizationPresetAdminActionType.OrganizationChanged,
        organizationID: organizationID
    }), []);

    const setPresetCount = useCallback((count: number) => dispatch({
        type: UICustomizationPresetAdminActionType.PresetCountChanged,
        count: count
    }), []);

    const setViewOptionState = useCallback((viewOptionState: number) => dispatch({
        type: UICustomizationPresetAdminActionType.ViewOptionStateChanged,
        viewOptionState: viewOptionState
    }), []);

    const clearCounts = useCallback(() => dispatch({
        type: UICustomizationPresetAdminActionType.ClearCountsChanged,
        payload: undefined
    }), []);

    return {
        state,
        setActiveTab,
        setOrganizationID,
        setPresetCount,
        setViewOptionState,
        clearCounts
    };
}

const UICustomizationPresetAdminContext = createContext<UseUICustomizationPresetAdminStateType | undefined>(undefined);

export type UICustomizationPresetAdminContextProviderProps = {
    children: ReactNode;
};

export default function UICustomizationPresetAdminContextProvider({ children }: UICustomizationPresetAdminContextProviderProps) {
    const pageUICustomizationPresetAdminState = useUICustomizationPresetAdminState();

    return (
        <UICustomizationPresetAdminContext.Provider value={pageUICustomizationPresetAdminState}>
            {children}
        </UICustomizationPresetAdminContext.Provider>
    );
}

export function useUICustomizationPresetAdminContext() {
    const context = useContext<UseUICustomizationPresetAdminStateType | undefined>(UICustomizationPresetAdminContext);
    if (!context) {
        throw new Error("useUICustomizationPresetAdminContext must be used within a UICustomizationPresetAdminContextProvider");
    }
    return context;
}